/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    getAssignmentMarksByGradeApi,
    getQuizDetailsByGradeCodeApi,
    getStudentSubjectsApi
} from "@/api/reportingApi";
import { GradeDetailInterface } from "@/ts/interfaces/Reporting/ReportingInterface";
import {
    mapGradeAssignmentMarksByTermWithApiData,
    mapQuizDetailsWithActivitiesApiData,
    mapStudentSubjectsApiData
} from "@/utils/reportingDataMapper";
import {
    calculateAssignmentSummaryPercentage,
    calculateLessonPercentage,
    calculateQuizesSummaryPercentage,
    calculateTotalLessons,
    mappingGradeDetailByTerm
} from "@/utils/reportingHelper";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { GradeDetailParams } from "../slices/gradeDetailSlice";

export interface GradeDetailResult {
    eukaId: string;
    gradeCode: string;
    details: GradeDetailInterface;
    quizzesSummary: any;
    lessonSummary: any;
    assignmentSummary: any;
    totalLessonCount: number;
    isQuizAvailable: boolean;
}

export const getGradeDetailThunk = createAsyncThunk<
    GradeDetailResult,
    GradeDetailParams,
    { rejectValue: string }
>("reporting/gradeDetail", async (params, { rejectWithValue }) => {
    try {
        const { eukaId, studentId, gradeCode } = params;

        // courses-details (DynamoDB) dropped — completion data is now embedded
        // directly in the subjects response (no_of_lessons_completed per subject,
        // started_at/completed_at per lesson). Only 3 parallel calls needed.
        // assignment-marks now accepts grade_code directly — no course-list pre-fetch.
        const [quizResult, assignmentResult, subjectResult] =
            await Promise.allSettled([
                getQuizDetailsByGradeCodeApi(eukaId, gradeCode),
                getAssignmentMarksByGradeApi(eukaId, gradeCode),
                getStudentSubjectsApi(studentId, gradeCode)
            ]);

        let hasQuizError = false;
        let hasAssignmentError = false;
        let hasSubjectError = false;

        const quizRes =
            quizResult.status === "fulfilled"
                ? quizResult.value
                : (() => {
                      hasQuizError = true;
                      return undefined;
                  })();
        const assignmentRes =
            assignmentResult.status === "fulfilled"
                ? assignmentResult.value
                : (() => {
                      hasAssignmentError = true;
                      return undefined;
                  })();
        const subjectRes =
            subjectResult.status === "fulfilled"
                ? subjectResult.value
                : (() => {
                      hasSubjectError = true;
                      return undefined;
                  })();

        const quizResponseData =
            quizRes?.data?.data && Array.isArray(quizRes.data.data)
                ? mapQuizDetailsWithActivitiesApiData(quizRes.data.data)
                : [];
        const isQuizAvailable: boolean = quizResponseData.length > 0;

        const assignmentResponseData =
            assignmentRes &&
            mapGradeAssignmentMarksByTermWithApiData(assignmentRes.data.data);

        const subjectResponseData =
            subjectRes && subjectRes.data && subjectRes.data.data
                ? mapStudentSubjectsApiData(
                      subjectRes.data.data,
                      gradeCode.toString()
                  )
                : {
                      terms: [],
                      electives: [],
                      additionalSubjects: [],
                      hasUniversityPathway: false
                  };

        const quizzesSummary = calculateQuizesSummaryPercentage(
            quizResponseData || []
        );
        const totalLessonCount = calculateTotalLessons(
            subjectResponseData || []
        );

        const details = mappingGradeDetailByTerm(
            subjectResponseData,
            quizResponseData || [],
            [],   // courseDetailsData — no longer used, completion comes from subjects
            assignmentResponseData || [],
            hasQuizError,
            false, // hasLessonError — always false, completion embedded in subjects
            hasAssignmentError,
            hasSubjectError
        );

        // lessonSummary: derive completed/total from the subject data directly
        // (noOfLessonsCompleted is now on each subject row from the backend).
        //
        // BLD1-869 guard: exempt subjects must not contribute to the COMPLETION
        // numerator. The denominator (totalLessonCount / calculateTotalLessons)
        // already excludes them because the backend sets lesson_count=0 for
        // exempt subjects — but historical completions on an exempt subject
        // would inflate the numerator → completed > total → % > 100%.
        // Applying the same isExempt filter here keeps both views consistent
        // with mappingGradeDetailByTerm which guards this inline.
        //
        // Electives + additional subjects (CYON, Drivers Ed, ...) must also be
        // included in the numerator — the denominator (calculateTotalLessons)
        // already counts their lesson totals, so omitting their completions
        // would understate the grade completion percentage.
        const termSubjectRows = subjectResponseData.terms.flatMap(term =>
            term.subjects
                .filter(subj => !subj.isExempt)
                .map(subj => ({
                    gradeCode: subj.gradeCode,
                    gradeSubjectId: subj.gradeSubjectId,
                    courseId: null,
                    subjectId: subj.id,
                    term: term.term,
                    name: subj.name,
                    noOfLessonsCompleted: subj.noOfLessonsCompleted ?? 0,
                    totalLessons: subj.lessonCount,
                    weeks: []
                }))
        );

        const electiveRows = subjectResponseData.electives.flatMap(elective =>
            elective.courses.map(course => ({
                gradeCode: course.gradeCode,
                gradeSubjectId: 0,
                courseId: course.id,
                subjectId: elective.subjectId,
                term: 999,
                name: course.name,
                noOfLessonsCompleted: course.noOfLessonsCompleted ?? 0,
                totalLessons: course.lessonCount,
                weeks: []
            }))
        );

        const additionalSubjectRows =
            subjectResponseData.additionalSubjects.map(subj => ({
                gradeCode: subj.gradeCode,
                gradeSubjectId: subj.gradeSubjectId,
                courseId: subj.courseId,
                subjectId: subj.subjectId,
                term: 999,
                name: subj.name,
                noOfLessonsCompleted: subj.noOfLessonsCompleted ?? 0,
                totalLessons: subj.lessonCount,
                weeks: []
            }));

        const lessonSummary = calculateLessonPercentage(
            [...termSubjectRows, ...electiveRows, ...additionalSubjectRows],
            totalLessonCount
        );

        const assignmentSummary = calculateAssignmentSummaryPercentage(
            assignmentResponseData || []
        );

        return {
            eukaId,
            gradeCode: gradeCode.toString(),
            details,
            quizzesSummary,
            lessonSummary,
            assignmentSummary,
            totalLessonCount,
            isQuizAvailable
        };
    } catch (err: any) {
        return rejectWithValue(err?.message ?? "Failed to load grade detail.");
    }
});
